import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Brain, ArrowLeft, Loader2, Download, Printer, RotateCcw, BookOpen, TrendingUp, Target, Lightbulb } from 'lucide-react';
import { aiLiteracyScanApi, AILiteracyAttempt } from '../api/aiLiteracyScan';
import { RadarChart } from '../components/ui/RadarChart';

export const AILiteracyResults: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [attempt, setAttempt] = useState<AILiteracyAttempt | null>(null);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    const loadAttemptDetails = async () => {
      try {
        setLoading(true);
        const data = await aiLiteracyScanApi.getAttemptWithQuestions(attemptId!);
        setAttempt(data.attempt);
        
        // Generate profile
        const generatedProfile = aiLiteracyScanApi.generateProfile(data.attempt, data.questions);
        setProfile(generatedProfile);
      } catch (err: any) {
        setError('Kon resultaten niet laden. Probeer het later opnieuw.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (attemptId) {
      loadAttemptDetails();
    }
  }, [attemptId]);

  const getScoreColor = (score: number) => {
    if (score >= 4.0) return 'text-green-600 bg-green-100';
    if (score >= 3.0) return 'text-blue-600 bg-blue-100';
    if (score >= 2.0) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 4.5) return { title: 'Expert Niveau', message: 'Je bent zeer vaardig in AI-geletterdheid' };
    if (score >= 4.0) return { title: 'Gevorderd', message: 'Je hebt een sterke basis in AI-geletterdheid' };
    if (score >= 3.0) return { title: 'Intermediair', message: 'Je hebt een goede basis, maar er is ruimte voor groei' };
    if (score >= 2.0) return { title: 'Beginnend', message: 'Je bent aan het begin van je AI-leerreis' };
    return { title: 'Starter', message: 'Er zijn veel mogelijkheden om je AI-vaardigheden te ontwikkelen' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Profiel laden...</p>
        </div>
      </div>
    );
  }

  if (error || !attempt || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Fout bij laden
            </h2>
            <p className="text-gray-600 mb-6">
              {error || 'Kon profiel niet laden'}
            </p>
            <button
              onClick={() => navigate('/ai-literacy-scan')}
              className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all"
            >
              Terug naar Scan
            </button>
          </div>
        </div>
      </div>
    );
  }

  const scoreInfo = getScoreMessage(profile.overallScore);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/ai-literacy-scan')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar AI Geletterdheid Scan
          </button>
          
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Jouw AI-Geletterdheid Profiel
            </h1>
            <p className="text-xl text-gray-600">
              Bekijk je sterke punten en ontwikkelingsmogelijkheden
            </p>
          </div>
        </div>

        {/* Overall Score with Radar Chart */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Radar Chart */}
            <div className="flex justify-center">
              <RadarChart 
                data={profile.categoryScores.map((cs: any) => ({
                  category: cs.category,
                  score: cs.score,
                  maxScore: 5
                }))}
                size={350}
                className="mb-6"
              />
            </div>
            
            {/* Score Info */}
            <div className="text-center lg:text-left">
              <div className={`inline-flex w-24 h-24 ${getScoreColor(profile.overallScore)} rounded-full items-center justify-center mx-auto lg:mx-0 mb-6`}>
                <div className="text-3xl font-bold">
                  {profile.overallScore}
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {scoreInfo.title}
              </h2>
              <p className="text-xl text-gray-600 mb-4">
                {scoreInfo.message}
              </p>
              <p className="text-gray-700">
                Gemiddelde score: <strong>{profile.overallScore}/5.0</strong>
              </p>
              
              {/* Score Interpretation */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">Score Interpretatie</h3>
                <p className="text-sm text-gray-600">
                  {profile.overallScore >= 4.5 && "Je hebt een expert-niveau in AI-geletterdheid en kunt anderen begeleiden."}
                  {profile.overallScore >= 4.0 && profile.overallScore < 4.5 && "Je hebt een gevorderd niveau en kunt complexe AI-taken uitvoeren."}
                  {profile.overallScore >= 3.0 && profile.overallScore < 4.0 && "Je hebt een solide basis maar er zijn nog ontwikkelingsmogelijkheden."}
                  {profile.overallScore >= 2.0 && profile.overallScore < 3.0 && "Je bent aan het begin van je AI-leerreis met veel groeipotentieel."}
                  {profile.overallScore < 2.0 && "Er zijn veel mogelijkheden om je AI-vaardigheden te ontwikkelen."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Scores */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Brain className="w-6 h-6 mr-3 text-purple-600" />
            Resultaten per Categorie
          </h2>
          
          <div className="space-y-4">
            {profile.categoryScores
              .sort((a: any, b: any) => b.score - a.score)
              .map((categoryScore: any) => (
                <div key={categoryScore.category} className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{categoryScore.category}</h3>
                    <span className={`text-lg font-bold px-3 py-1 rounded-full ${getScoreColor(categoryScore.score)}`}>
                      {categoryScore.score}/5
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${
                        categoryScore.score >= 4.0 ? 'bg-green-500' :
                        categoryScore.score >= 3.0 ? 'bg-blue-500' :
                        categoryScore.score >= 2.0 ? 'bg-amber-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(categoryScore.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Insights */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Strengths */}
          {profile.strengths.length > 0 && (
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-green-600" />
                Je Sterke Punten
              </h3>
              <ul className="space-y-2">
                {profile.strengths.map((strength: string, index: number) => (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Weaknesses */}
          {profile.weaknesses.length > 0 && (
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
                Ontwikkelingsmogelijkheden
              </h3>
              <ul className="space-y-2">
                {profile.weaknesses.map((weakness: string, index: number) => (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">{weakness}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Recommendations */}
        {profile.recommendations.length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-blue-600" />
              Aanbevelingen
            </h3>
            <ul className="space-y-3">
              {profile.recommendations.map((recommendation: string, index: number) => (
                <li key={index} className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-blue-600 text-sm font-bold">{index + 1}</span>
                  </div>
                  <span className="text-gray-700">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
              <Download className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">Download PDF</span>
            </button>
            <button className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
              <Download className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">Download PNG</span>
            </button>
            <button className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
              <Printer className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">Print</span>
            </button>
            <button 
              onClick={() => navigate('/ai-literacy-scan')}
              className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
            >
              <RotateCcw className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">Opnieuw</span>
            </button>
          </div>
        </div>

        {/* Training CTA */}
        <div className="mt-8 text-center">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Wil je je AI-vaardigheden verder ontwikkelen?</h3>
            <p className="text-purple-100 mb-6">
              Bekijk onze AI-training modules en verbeter je geletterdheid nog verder
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/ai-training')}
                className="px-8 py-4 bg-white text-purple-700 rounded-2xl font-semibold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl"
              >
                <BookOpen className="w-5 h-5 mr-2 inline" />
                Bekijk AI Training
              </button>
              <button
                onClick={() => navigate('/self-assessment')}
                className="px-8 py-4 bg-purple-500 text-white rounded-2xl font-semibold hover:bg-purple-400 transition-all"
              >
                <Brain className="w-5 h-5 mr-2 inline" />
                Doe AI Kennistoets
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
